'use client'

import { useMutation } from '@apollo/client'
import { Dialog, DialogContent } from '@chain-zen/ui'
import { useState } from 'react'
import { toast } from 'sonner'

import { UPDATE_WORKSPACE } from '../../data/mutations/workspace'
import { GET_WORKSPACE } from '../../data/queries/workspace'
import type { IWorkspace } from '../../types/workspace'
import { Section } from '../shared/section/section'
import { WorkspaceDetailsForm } from './workspace-details-form'

/**
 * Workspace details section component
 * Displays workspace information and allows for editing
 */
export const WorkspaceDetailsSection = ({ workspaceInfo }: { workspaceInfo: IWorkspace }) => {
  const [isOpen, setIsOpen] = useState(false)
  const displayName = workspaceInfo?.displayName

  const [updateWorkspace, { loading }] = useMutation(UPDATE_WORKSPACE, {
    onCompleted: () => {
      toast.success('Workspace details updated successfully')
      setIsOpen(false)
    },
    onError: (error) => {
      toast.error(`Failed to update workspace details: ${error.message}`)
    },
    refetchQueries: [GET_WORKSPACE]
  })

  const handleSubmit = async (displayName: string) => {
    try {
      const input = {
        displayName: displayName.trim()
      }
      updateWorkspace({
        context: {
          headers: {
            'ws-id': workspaceInfo.id
          }
        },
        variables: input
      })
    } catch (error) {
      console.error('Error updating workspace details:', error)
    }
  }

  return (
    <>
      <Section
        sectionTitle="Workspace Details"
        multipleFields={[
          {
            fieldName: 'Workspace Name:',
            value: displayName || ''
          }
        ]}
        buttonName="Edit details"
        onClick={() => setIsOpen(true)}
      />

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <WorkspaceDetailsForm
            initialValues={{ displayName: displayName || '' }}
            onSubmitAction={handleSubmit}
            onCancelAction={() => setIsOpen(false)}
            isLoading={loading}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
